/**
 * FieldBuilder — the Settings and Inputs a person fills in for one tool.
 *
 * Settings are asked once, at setup; Inputs are asked each run. Those are the
 * words a person sees in Nucleus, so the builder uses them rather than the
 * stored `setup` / `runtime`.
 *
 * THE TYPE VOCABULARY IS FETCHED (`GET /plugins/field-types`), so the builder
 * never offers a type the backend refuses. The checks below are the backend's
 * own rules, said while the developer is still editing.
 */
import { useEffect, useMemo, useState } from 'react';
import { SegmentedControl, Toggle } from '@ofself/shell';

import api from '../../services/api';
import { SCOPE_WORDS, typeWords } from './manifest';
import './FieldBuilder.css';

/** A blank field. Short text, asked once, is the least surprising start. */
const blankField = (n) => ({
  key: `field_${n}`,
  label: '',
  description: '',
  type: 'text',
  scope: 'setup',
  required: false,
  secret: false,
});

/** Problems with one field. The backend's rules, said sooner. */
function problemsFor(f, meta, allKeys) {
  const out = [];
  const spec = meta[f.type] || {};
  if (!f.key || !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(f.key)) {
    out.push('Key must start with a letter and contain only letters, numbers and underscores.');
  }
  if (allKeys.filter((k) => k === f.key).length > 1) out.push('Two of them share this key.');
  if (!String(f.label || '').trim()) out.push('A label is what the person filling it in reads first.');
  // Not a backend rule: a required field with no explanation is the one a
  // person cannot fill without guessing, and the author is the only one who knows.
  if (f.required && !String(f.description || '').trim()) {
    out.push('A required one needs a description — you are the only one who knows what it wants.');
  }
  if ((spec.supports || []).includes('options') && !(f.options || []).length) {
    out.push('This type needs at least one option.');
  }
  if (f.type === 'node' && !String(f.schema || '').trim()) {
    out.push('Name a schema, or a picker would offer the person their entire graph.');
  }
  if (f.secret && spec.can_be_secret === false) {
    out.push(`${typeWords(f.type)} cannot be a key.`);
  }
  if (f.secret && f.scope === 'runtime') {
    out.push('A key is asked once, under Keys, and never passed on a run, so it cannot be an input.');
  }
  if (f.secret && f.default !== undefined && String(f.default) !== '') {
    out.push('A key cannot carry a default — a default is a value, and it would ship in every listing.');
  }
  if (f.min !== undefined && f.max !== undefined
      && f.min !== '' && f.max !== '' && Number(f.min) > Number(f.max)) {
    out.push('Minimum is greater than maximum.');
  }
  return out;
}

const SCOPES = [
  { value: 'setup', label: 'Settings · asked once' },
  { value: 'runtime', label: 'Inputs · asked each run' },
];

/* The field types are the same for every builder on the page, so they are
   fetched once. */
let typesOnce = null;
function useFieldTypes() {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let live = true;
    typesOnce = typesOnce || api.request('/plugins/field-types')
      .then((r) => {
        const byType = {};
        for (const t of r?.types || []) byType[t.type] = t;
        return byType;
      })
      .catch(() => { typesOnce = null; return {}; });
    typesOnce.then((m) => { if (live) setMeta(m); });
    return () => { live = false; };
  }, []);
  return meta;
}

export default function FieldBuilder({ value, onChange, disabled, schemas = [], canHoldCredentials = true }) {
  const fields = useMemo(() => value || [], [value]);
  const meta = useFieldTypes();
  const [openIndex, setOpenIndex] = useState(null);

  const problems = useMemo(() => {
    if (!meta) return fields.map(() => []);
    const keys = fields.map((f) => f.key);
    return fields.map((f) => problemsFor(f, meta, keys));
  }, [fields, meta]);

  const set = (i, patch) => onChange(fields.map((f, n) => {
    if (n !== i) return f;
    const next = { ...f, ...patch };
    for (const k of Object.keys(patch)) if (patch[k] === undefined) delete next[k];
    return next;
  }));
  const add = () => { onChange([...fields, blankField(fields.length + 1)]); setOpenIndex(fields.length); };
  const remove = (i) => { onChange(fields.filter((_, n) => n !== i)); setOpenIndex(null); };
  const move = (i, by) => {
    const j = i + by;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
    setOpenIndex(j);
  };

  if (!meta) return <p className="fb__muted">Loading…</p>;

  const offerable = Object.values(meta).filter((t) => t.offerable).map((t) => t.type);

  return (
    <div className="fb">
      {fields.length === 0 ? <p className="fb__none">None.</p> : null}

      <ol className="fb__list">
        {fields.map((f, i) => {
          const spec = meta[f.type] || {};
          const supports = spec.supports || [];
          const open = openIndex === i;
          const bad = problems[i];
          return (
            <li key={i} className={`fb__item${open ? ' is-open' : ''}${bad.length ? ' is-bad' : ''}`}>
              <div className="fb__row">
                <button type="button" className="fb__summary" onClick={() => setOpenIndex(open ? null : i)}
                        aria-expanded={open}>
                  <span className="fb__lab">{f.label || <em>No label</em>}</span>
                  <span className="fb__tags">
                    <span className="fb__tag">{SCOPE_WORDS[f.scope || 'setup']}</span>
                    <span className="fb__tag">{typeWords(f.type)}</span>
                    {f.required ? <span className="fb__tag is-strong">Required</span> : null}
                    {f.secret ? <span className="fb__tag is-strong">Key</span> : null}
                  </span>
                  <code className="fb__key">{f.key}</code>
                </button>
                <span className="fb__tools">
                  <button type="button" className="fb__icon" aria-label="Move up"
                          disabled={disabled || i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" className="fb__icon" aria-label="Move down"
                          disabled={disabled || i === fields.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button type="button" className="fb__icon fb__icon--del" aria-label="Remove"
                          disabled={disabled} onClick={() => remove(i)}>×</button>
                </span>
              </div>

              {bad.length ? (
                <ul className="fb__problems">{bad.map((p, n) => <li key={n}>{p}</li>)}</ul>
              ) : null}

              {open ? (
                <div className="fb__edit">
                  <div className="fb__grid">
                    <label className="fb__f">
                      <span>Label</span>
                      <input className="fb__in" value={f.label} disabled={disabled}
                             placeholder="Chess.com username"
                             onChange={(e) => set(i, { label: e.target.value })} />
                    </label>
                    <label className="fb__f">
                      <span>Key</span>
                      <input className="fb__in fb__in--mono" value={f.key} disabled={disabled}
                             onChange={(e) => set(i, { key: e.target.value })} />
                    </label>
                  </div>

                  <label className="fb__f">
                    <span>Description</span>
                    <textarea className="fb__in" rows={2} value={f.description || ''} disabled={disabled}
                              placeholder="Where to find this value, and what happens if it is wrong."
                              onChange={(e) => set(i, { description: e.target.value })} />
                  </label>

                  <div className="fb__f">
                    <span>Asked</span>
                    <SegmentedControl
                      options={SCOPES}
                      value={f.scope || 'setup'}
                      onChange={(v) => { if (!disabled && !(f.secret && v === 'runtime')) set(i, { scope: v }); }}
                    />
                  </div>

                  <label className="fb__f">
                    <span>Kind of answer</span>
                    <select className="fb__in" value={f.type} disabled={disabled}
                            onChange={(e) => set(i, { type: e.target.value })}>
                      {offerable.map((t) => (
                        <option key={t} value={t}>{typeWords(t)}</option>
                      ))}
                      {!offerable.includes(f.type) ? (
                        <option value={f.type}>{typeWords(f.type)} (older)</option>
                      ) : null}
                    </select>
                  </label>

                  <div className="fb__switches">
                    <Toggle
                      checked={!!f.required}
                      disabled={disabled}
                      onChange={(e) => set(i, { required: e.target.checked })}
                      label="Required"
                    />
                    <Toggle
                      checked={!!f.secret}
                      disabled={disabled || spec.can_be_secret === false || (!canHoldCredentials && !f.secret)}
                      onChange={(e) => set(i, {
                        secret: e.target.checked,
                        // A key is asked once and never has a default.
                        ...(e.target.checked ? { scope: 'setup', default: undefined } : {}),
                      })}
                      label="Key · locked in their browser to your public key"
                    />
                  </div>

                  {supports.includes('options') ? (
                    <label className="fb__f">
                      <span>Options · one per line</span>
                      <textarea className="fb__in" rows={3} disabled={disabled}
                                value={(f.options || []).map((o) => (typeof o === 'object' ? `${o.value}|${o.label}` : o)).join('\n')}
                                placeholder={'games\npuzzles|Puzzles you solved'}
                                onChange={(e) => set(i, {
                                  options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean)
                                    .map((s) => (s.includes('|')
                                      ? { value: s.split('|')[0].trim(), label: s.split('|').slice(1).join('|').trim() }
                                      : s)),
                                })} />
                    </label>
                  ) : null}

                  {supports.includes('min') ? (
                    <div className="fb__grid">
                      <label className="fb__f">
                        <span>Smallest</span>
                        <input className="fb__in" type="number" disabled={disabled}
                               value={f.min ?? ''}
                               onChange={(e) => set(i, { min: e.target.value === '' ? undefined : Number(e.target.value) })} />
                      </label>
                      <label className="fb__f">
                        <span>Largest</span>
                        <input className="fb__in" type="number" disabled={disabled}
                               value={f.max ?? ''}
                               onChange={(e) => set(i, { max: e.target.value === '' ? undefined : Number(e.target.value) })} />
                      </label>
                    </div>
                  ) : null}

                  {supports.includes('max_length') ? (
                    <label className="fb__f">
                      <span>Longest, in characters</span>
                      <input className="fb__in" type="number" min="1" disabled={disabled}
                             value={f.max_length ?? ''}
                             onChange={(e) => set(i, { max_length: e.target.value === '' ? undefined : Number(e.target.value) })} />
                    </label>
                  ) : null}

                  {supports.includes('schema') ? (
                    <label className="fb__f">
                      <span>Which schema</span>
                      <select className="fb__in" value={f.schema || ''} disabled={disabled}
                              onChange={(e) => set(i, { schema: e.target.value || undefined })}>
                        <option value="">Choose a schema</option>
                        {f.schema && !schemas.some((s) => s.schema_name === f.schema)
                          ? <option value={f.schema}>{f.schema}</option> : null}
                        {schemas.map((s) => (
                          <option key={s.id} value={s.schema_name}>{s.display || s.schema_name}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}

                  {!f.secret ? (
                    <label className="fb__f">
                      <span>Default</span>
                      <input className="fb__in" value={f.default ?? ''} disabled={disabled}
                             onChange={(e) => set(i, { default: e.target.value === '' ? undefined : e.target.value })} />
                    </label>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>

      <button type="button" className="fb__add" onClick={add} disabled={disabled}>
        Add a setting or input
      </button>
    </div>
  );
}

export { blankField, problemsFor };
